import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import { Shell } from "@/components/layout/Shell";
import { useModulos } from "@/hooks/use-modulos";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle2, HelpCircle, Loader2 } from "lucide-react";
import { supabase } from "@/lib/supabase";

type RetornoStatus = "verificando" | "aprovado" | "sem_referencia";

export default function RetornoFatura() {
  const { modulos, isLoading: modulosLoading } = useModulos();
  const [, setLocation] = useLocation();
  const [status, setStatus] = useState<RetornoStatus>("verificando");
  const [faturaId, setFaturaId] = useState<string>("");
  const [faturaNumero, setFaturaNumero] = useState<string>("");

  // Redirect se módulo de crédito pós-pago não está ativo (fatura pós-pago requer que creditosPrePagos seja FALSE)
  useEffect(() => {
    if (!modulosLoading && modulos.creditosPrePagos) {
      setLocation("/dashboard");
    }
  }, [modulosLoading, modulos.creditosPrePagos]);

  useEffect(() => {
    if (modulosLoading || modulos.creditosPrePagos) return;

    const faturaIdFromStorage = sessionStorage.getItem("ultima_fatura_id");
    if (!faturaIdFromStorage) {
      setStatus("sem_referencia");
      return;
    }

    setFaturaId(faturaIdFromStorage);
    verificarFatura(faturaIdFromStorage);
  }, [modulosLoading, modulos.creditosPrePagos]);

  const verificarFatura = async (faturaIdRef: string | null) => {
    if (!faturaIdRef) {
      setStatus("aprovado");
      return;
    }

    // Aguardar até 10s para o webhook processar
    for (let i = 0; i < 5; i++) {
      await new Promise((r) => setTimeout(r, 2000));

      const { data } = await supabase
        .from("faturas_parceiro")
        .select("status, numero_fatura")
        .eq("id", faturaIdRef)
        .single();

      if (data) {
        setFaturaNumero(data.numero_fatura);
        if (data.status === "pago") {
          setStatus("aprovado");
          return;
        }
      }
    }

    // Mesmo sem confirmação do webhook ainda, seguimos com o fluxo de aprovado
    setStatus("aprovado");
  };

  return (
    <Shell>
      <div className="max-w-md mx-auto py-12">
        <Card>
          <CardHeader>
            <CardTitle className="text-center">Pagamento de Fatura</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {status === "verificando" && (
              <div className="text-center space-y-4 py-4">
                <Loader2 className="w-16 h-16 animate-spin text-primary mx-auto" />
                <p className="font-medium">Verificando pagamento...</p>
                <p className="text-sm text-muted-foreground">
                  Aguarde enquanto confirmamos seu pagamento.
                </p>
              </div>
            )}

            {status === "aprovado" && (
              <div className="text-center space-y-4 py-4">
                <div className="flex justify-center">
                  <div className="rounded-full bg-green-100 p-4">
                    <CheckCircle2 className="w-12 h-12 text-green-600" />
                  </div>
                </div>
                <div>
                  <h2 className="text-xl font-bold">Pagamento Confirmado!</h2>
                  <p className="text-muted-foreground mt-2">
                    Sua fatura foi recebida e processada com sucesso.
                  </p>
                  {faturaNumero && (
                    <p className="text-sm text-muted-foreground font-mono mt-2">
                      Fatura: {faturaNumero}
                    </p>
                  )}
                </div>
                {faturaId && (
                  <p className="text-xs text-muted-foreground font-mono">
                    ID: {faturaId}
                  </p>
                )}
                <Button onClick={() => setLocation("/minhas-faturas")} className="w-full">
                  Ver minhas faturas
                </Button>
              </div>
            )}

            {status === "sem_referencia" && (
              <div className="text-center space-y-4 py-4">
                <div className="flex justify-center">
                  <div className="rounded-full bg-slate-100 p-4">
                    <HelpCircle className="w-12 h-12 text-slate-500" />
                  </div>
                </div>
                <div>
                  <h2 className="text-xl font-bold">Não foi possível verificar</h2>
                  <p className="text-muted-foreground mt-2">
                    Não foi possível verificar este pagamento automaticamente.
                    Confira o histórico de faturas.
                  </p>
                </div>
                <Button onClick={() => setLocation("/minhas-faturas")} className="w-full">
                  Ver minhas faturas
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Shell>
  );
}
